/**
 * ProfileOptimizerPage - KI-gestützte Profilanalyse für Social Media
 * 
 * Features:
 * - Plattform-Auswahl (Instagram, Facebook, LinkedIn, Google Business)
 * - API-Datenabruf mit Formular-Fallback
 * - KI-Analyse mit Claude
 * - Score-Visualisierung (Clarity, Targeting, Trust, CTA, Visuals)
 * - Konkrete Verbesserungsvorschläge
 * - Bio-Vorschläge
 */

import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from 'sonner';
import { 
  User, RefreshCw, Loader2, Sparkles, Check, 
  Facebook, Instagram, Linkedin, ChevronRight, Copy,
  AlertTriangle, ExternalLink, Eye, Target, Heart,
  MessageSquare, Image, TrendingUp, History, Star,
  Users, Plus, Trash2, Scale
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform-Liste (konstant)
const PLATFORM_LIST = [
  { id: 'instagram', name: 'Instagram' },
  { id: 'facebook', name: 'Facebook Page' },
  { id: 'linkedin', name: 'LinkedIn' },
  { id: 'google_business', name: 'Google Business' }
];

// Platform Icons
const PlatformIcon = ({ platform, size = 24 }) => {
  switch (platform) {
    case 'facebook': return <Facebook size={size} className="text-blue-600" />;
    case 'instagram': return <Instagram size={size} className="text-pink-500" />;
    case 'linkedin': return <Linkedin size={size} className="text-blue-700" />;
    case 'google_business': return <Star size={size} className="text-amber-500" />;
    default: return <User size={size} className="text-gray-500" />;
  }
};

// Platform Card für Auswahl
const PlatformCard = ({ platform, data, isSelected, onClick }) => {
  return (
    <button
      onClick={onClick}
      data-testid={`platform-card-${platform.id}`}
      className={`p-4 rounded-xl border-2 transition-all text-left ${
        isSelected 
          ? 'border-orange-500 bg-orange-50 shadow-lg' 
          : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50/50'
      }`}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
          data.connected ? 'bg-green-100' : 'bg-gray-100'
        }`}>
          <PlatformIcon platform={platform.id} size={28} />
        </div>
        <div>
          <h3 className="font-semibold text-gray-800">{platform.name}</h3>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            data.connected 
              ? 'bg-green-100 text-green-700' 
              : 'bg-amber-100 text-amber-700'
          }`}>
            {data.connected ? 'Verbunden' : 'Formular'}
          </span>
        </div>
      </div>
      {data.last_analysis && (
        <p className="text-xs text-gray-500 mt-1">
          Letzte Analyse: {new Date(data.last_analysis).toLocaleDateString('de-DE')}
        </p>
      )}
    </button>
  );
};

// Score Bar Komponente
const ScoreBar = ({ label, score, icon: Icon, description }) => {
  const getColor = (score) => {
    if (score >= 8) return 'bg-green-500';
    if (score >= 6) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-gray-500" />
          <span className="text-sm font-medium text-gray-700">{label}</span>
        </div>
        <span className={`text-sm font-bold ${
          score >= 8 ? 'text-green-600' : score >= 6 ? 'text-amber-600' : 'text-red-600'
        }`}>
          {score}/10
        </span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${getColor(score)} transition-all duration-500`}
          style={{ width: `${score * 10}%` }}
        />
      </div>
      {description && (
        <p className="text-xs text-gray-500 mt-1">{description}</p>
      )}
    </div>
  );
};

// Profil-Formular für manuelle Eingabe
const ProfileForm = ({ platform, initialData, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    bio: initialData?.bio || '',
    description: initialData?.description || '',
    website: initialData?.website || '',
    category: initialData?.category || '',
    followers: initialData?.followers || 0,
    posts_count: initialData?.posts_count || 0,
    has_profile_image: !!initialData?.profile_image,
    has_banner_image: !!initialData?.banner_image
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
        <AlertTriangle size={16} className="inline mr-2" />
        API nicht verbunden. Bitte fülle das Formular mit deinen Profildaten aus.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Profilname *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="z.B. Förderfuchs"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            data-testid="profile-name-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Website/Link</label>
          <input
            type="url"
            value={formData.website}
            onChange={(e) => handleChange('website', e.target.value)}
            placeholder="https://foerderfuchs-bildung.de"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Bio / Headline *</label>
        <textarea
          value={formData.bio}
          onChange={(e) => handleChange('bio', e.target.value)}
          placeholder="Deine aktuelle Bio hier einfügen..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          data-testid="profile-bio-input"
        />
        <p className="text-xs text-gray-500 mt-1">{formData.bio.length}/200 Zeichen</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung (optional)</label>
        <textarea
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Ausführliche Beschreibung (falls vorhanden)..."
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Kategorie</label>
          <select
            value={formData.category}
            onChange={(e) => handleChange('category', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="">Bitte wählen</option>
            <option value="Bildung">Bildung</option>
            <option value="Coaching">Coaching</option>
            <option value="Beratung">Beratung</option>
            <option value="Dienstleistung">Dienstleistung</option>
            <option value="Anderes">Anderes</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Follower (ca.)</label>
          <input
            type="number"
            value={formData.followers}
            onChange={(e) => handleChange('followers', parseInt(e.target.value) || 0)}
            min="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Beiträge</label>
          <input
            type="number"
            value={formData.posts_count}
            onChange={(e) => handleChange('posts_count', parseInt(e.target.value) || 0)}
            min="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>
      </div>

      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.has_profile_image}
            onChange={(e) => handleChange('has_profile_image', e.target.checked)}
            className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
          />
          <span className="text-sm text-gray-700">Profilbild vorhanden</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.has_banner_image}
            onChange={(e) => handleChange('has_banner_image', e.target.checked)}
            className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
          />
          <span className="text-sm text-gray-700">Banner/Cover vorhanden</span>
        </label>
      </div>

      <button
        onClick={() => onSubmit(formData)}
        disabled={loading || !formData.name || !formData.bio}
        className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="analyze-profile-btn"
      >
        {loading ? (
          <>
            <Loader2 className="animate-spin" size={18} />
            Analysiere Profil...
          </>
        ) : (
          <>
            <Sparkles size={18} />
            Profil analysieren
          </>
        )}
      </button>
    </div>
  );
};

// Analyse-Ergebnis Komponente
const AnalysisResult = ({ analysis, platform }) => {
  const [copiedBio, setCopiedBio] = useState(null);

  const copyBio = (bio, index) => {
    navigator.clipboard.writeText(bio);
    setCopiedBio(index);
    toast.success('Bio kopiert!');
    setTimeout(() => setCopiedBio(null), 2000);
  };

  const scores = analysis?.scores || {};
  const scoreLabels = {
    clarity: { label: 'Klarheit', icon: Eye, desc: 'Ist auf den ersten Blick klar, was du anbietest?' },
    targeting: { label: 'Zielgruppe', icon: Target, desc: 'Sprichst du deine Zielgruppe direkt an?' },
    trust: { label: 'Vertrauen', icon: Heart, desc: 'Baust du Social Proof und Seriosität auf?' },
    cta: { label: 'Call-to-Action', icon: MessageSquare, desc: 'Gibt es eine klare Handlungsaufforderung?' },
    visuals: { label: 'Visuelles', icon: Image, desc: 'Wie professionell wirkt dein Auftritt?' }
  };

  const overallScore = Object.values(scores).reduce((a, b) => a + b, 0) / Object.keys(scores).length || 0;

  return (
    <div className="space-y-6">
      {/* Overall Score */}
      <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium opacity-90">Gesamt-Score</h3>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-5xl font-bold">{overallScore.toFixed(1)}</span>
              <span className="text-xl opacity-80">/10</span>
            </div>
          </div>
          <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
            overallScore >= 8 ? 'bg-green-400/30' : overallScore >= 6 ? 'bg-amber-400/30' : 'bg-red-400/30'
          }`}>
            <TrendingUp size={40} />
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Sparkles className="text-orange-500" size={18} />
          KI-Zusammenfassung
        </h3>
        <p className="text-gray-600 leading-relaxed">{analysis?.summary}</p>
      </div>

      {/* Scores */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-800 mb-4">Bewertung nach Kriterien</h3>
        {Object.entries(scores).map(([key, score]) => {
          const config = scoreLabels[key];
          return config ? (
            <ScoreBar 
              key={key}
              label={config.label}
              score={score}
              icon={config.icon}
              description={config.desc}
            />
          ) : null;
        })}
      </div>

      {/* Improvements */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <AlertTriangle className="text-amber-500" size={18} />
          Verbesserungsvorschläge
        </h3>
        <ul className="space-y-3">
          {(analysis?.improvements || []).map((improvement, index) => (
            <li key={index} className="flex items-start gap-3">
              <span className="w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium">
                {index + 1}
              </span>
              <p className="text-gray-600">{improvement}</p>
            </li>
          ))}
        </ul>
      </div>

      {/* Suggested Bios */}
      {analysis?.suggested_bios?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <MessageSquare className="text-blue-500" size={18} />
            Bio-Vorschläge
          </h3>
          <div className="space-y-3">
            {analysis.suggested_bios.map((bio, index) => (
              <div 
                key={index} 
                className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-orange-300 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-gray-700 text-sm flex-1">{bio}</p>
                  <button
                    onClick={() => copyBio(bio, index)}
                    className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                      copiedBio === index 
                        ? 'bg-green-100 text-green-600' 
                        : 'hover:bg-gray-200 text-gray-500'
                    }`}
                    data-testid={`copy-bio-${index}`}
                  >
                    {copiedBio === index ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2">{bio.length} Zeichen</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Hauptkomponente
const ProfileOptimizerPage = () => {
  const [platforms, setPlatforms] = useState({});
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingProfile, setFetchingProfile] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Wettbewerber-Vergleich State
  const [activeTab, setActiveTab] = useState('analyze'); // 'analyze' | 'compare'
  const [compareLoading, setCompareLoading] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [ownProfileForCompare, setOwnProfileForCompare] = useState({
    useHistory: false,
    selectedHistoryId: '',
    name: '',
    bio: '',
    description: '',
    website: '',
    category: '',
    platform: ''
  });
  const [competitors, setCompetitors] = useState([
    { label: 'Wettbewerber 1', name: '', bio: '', description: '', url: '' }
  ]);

  // Lade Plattform-Status beim Start
  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        const response = await axios.get(`${API}/profile-optimizer/platforms`);
        setPlatforms(response.data.platforms || {});
      } catch (error) {
        // Fallback mit default Status
        const defaultPlatforms = {};
        PLATFORM_LIST.forEach(p => {
          defaultPlatforms[p.id] = { 
            name: p.name, 
            connected: false, 
            can_analyze: true,
            last_analysis: null 
          };
        });
        setPlatforms(defaultPlatforms);
      }
    };
    fetchPlatforms();
  }, []);

  // Lade Historie
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await axios.get(`${API}/profile-optimizer/history?limit=5`);
        setHistory(response.data.analyses || []);
      } catch (error) {
        { /* Fetch fehlgeschlagen – kein kritischer Fehler */ }
      }
    };
    fetchHistory();
  }, [analysis]); // Refresh nach neuer Analyse

  // Plattform auswählen und Daten laden
  const handleSelectPlatform = async (platformId) => {
    setSelectedPlatform(platformId);
    setAnalysis(null);
    setProfileData(null);
    
    const platformInfo = platforms[platformId];
    
    if (platformInfo?.connected) {
      setFetchingProfile(true);
      try {
        const response = await axios.get(`${API}/profile-optimizer/fetch/${platformId}`);
        if (response.data.fetched_via_api) {
          setProfileData(response.data.data);
          toast.success('Profildaten geladen!');
        }
      } catch (error) {
        toast.error('Fehler beim Laden der Profildaten');
      } finally {
        setFetchingProfile(false);
      }
    }
  };

  // Analyse durchführen
  const handleAnalyze = async (formData = null) => {
    if (!selectedPlatform) {
      toast.error('Bitte wähle eine Plattform');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        platform: selectedPlatform,
        use_api: !!platforms[selectedPlatform]?.connected && !formData
      };

      if (formData) {
        payload.profile_data = formData;
      }

      const response = await axios.post(`${API}/profile-optimizer/analyze`, payload);
      
      if (response.data.success) {
        setAnalysis(response.data.analysis);
        toast.success('Analyse abgeschlossen!');
      } else {
        throw new Error(response.data.error || 'Analyse fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Fehler bei der Analyse: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Mit API-Daten analysieren
  const handleAnalyzeWithApi = () => {
    handleAnalyze(null);
  };

  // === Wettbewerber-Vergleich Funktionen ===
  
  const addCompetitor = () => {
    if (competitors.length < 3) {
      setCompetitors([
        ...competitors,
        { label: `Wettbewerber ${competitors.length + 1}`, name: '', bio: '', description: '', url: '' }
      ]);
    }
  };

  const removeCompetitor = (index) => {
    if (competitors.length > 1) {
      setCompetitors(competitors.filter((_, i) => i !== index));
    }
  };

  const updateCompetitor = (index, field, value) => {
    const updated = [...competitors];
    updated[index][field] = value;
    setCompetitors(updated);
  };

  const updateOwnProfile = (field, value) => {
    setOwnProfileForCompare(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectHistoryForCompare = (historyItem) => {
    setOwnProfileForCompare({
      useHistory: true,
      selectedHistoryId: historyItem.analyzed_at,
      name: historyItem.profile_data?.name || '',
      bio: historyItem.profile_data?.bio || '',
      description: historyItem.profile_data?.description || '',
      website: historyItem.profile_data?.website || '',
      category: historyItem.profile_data?.category || '',
      platform: historyItem.platform || ''
    });
  };

  const handleCompare = async () => {
    // Validierung
    const hasOwnProfile = ownProfileForCompare.name || ownProfileForCompare.bio;
    if (!hasOwnProfile && !ownProfileForCompare.useHistory) {
      toast.error('Bitte gib dein eigenes Profil ein oder wähle eine Analyse aus der Historie');
      return;
    }

    const validCompetitors = competitors.filter(c => c.bio || c.description);
    if (validCompetitors.length === 0) {
      toast.error('Bitte gib mindestens einen Wettbewerber mit Bio/Beschreibung ein');
      return;
    }

    setCompareLoading(true);
    try {
      const payload = {
        own_profile: {
          profile_id: ownProfileForCompare.useHistory ? ownProfileForCompare.selectedHistoryId : null,
          platform: ownProfileForCompare.platform,
          name: ownProfileForCompare.name,
          bio: ownProfileForCompare.bio,
          description: ownProfileForCompare.description,
          website: ownProfileForCompare.website,
          category: ownProfileForCompare.category
        },
        competitors: validCompetitors.map((c, i) => ({
          label: c.label || `Wettbewerber ${i + 1}`,
          name: c.name,
          bio: c.bio,
          description: c.description,
          url: c.url
        }))
      };

      const response = await axios.post(`${API}/profile-optimizer/compare`, payload);
      
      if (response.data.success) {
        setComparisonResult(response.data);
        toast.success('Vergleich abgeschlossen!');
      } else {
        throw new Error(response.data.error || 'Vergleich fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Fehler beim Vergleich: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCompareLoading(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="profile-optimizer-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-900">Profile Optimizer</h1>
          <p className="text-gray-500 mt-1">Analysiere und optimiere deine Social-Media-Profile mit KI</p>
        </div>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            showHistory 
              ? 'bg-orange-100 text-orange-700' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          data-testid="toggle-history-btn"
        >
          <History size={18} />
          Historie
        </button>
      </div>

      {/* Tab-Navigation */}
      <div className="flex gap-2 p-1 bg-gray-100 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('analyze')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all ${
            activeTab === 'analyze'
              ? 'bg-white text-orange-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
          data-testid="tab-analyze"
        >
          <Sparkles size={18} />
          Profil analysieren
        </button>
        <button
          onClick={() => setActiveTab('compare')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all ${
            activeTab === 'compare'
              ? 'bg-white text-orange-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
          data-testid="tab-compare"
        >
          <Scale size={18} />
          Wettbewerber-Vergleich
          <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded">NEU</span>
        </button>
      </div>

      {/* Historie-Panel */}
      {showHistory && history.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-800 mb-4">Letzte Analysen</h2>
          <div className="space-y-3">
            {history.map((item, index) => (
              <div 
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <PlatformIcon platform={item.platform} size={20} />
                  <div>
                    <p className="font-medium text-gray-800 capitalize">{item.platform}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(item.analyzed_at).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">
                    Score: {(Object.values(item.analysis?.scores || {}).reduce((a, b) => a + b, 0) / 5).toFixed(1)}/10
                  </span>
                  <ChevronRight size={16} className="text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============ PROFIL ANALYSIEREN TAB ============ */}
      {activeTab === 'analyze' && (
      <>
      {/* Plattform-Auswahl */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="font-semibold text-gray-800 mb-4">1. Plattform auswählen</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PLATFORM_LIST.map(platform => (
            <PlatformCard
              key={platform.id}
              platform={platform}
              data={platforms[platform.id] || { connected: false, last_analysis: null }}
              isSelected={selectedPlatform === platform.id}
              onClick={() => handleSelectPlatform(platform.id)}
            />
          ))}
        </div>
      </div>

      {/* Profil-Input oder Analyse-Button */}
      {selectedPlatform && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-800 mb-4">2. Profildaten eingeben oder laden</h2>
          
          {fetchingProfile ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-orange-500" size={32} />
              <span className="ml-3 text-gray-500">Lade Profildaten...</span>
            </div>
          ) : platforms[selectedPlatform]?.connected && profileData ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Check className="text-green-500" size={20} />
                  <div>
                    <p className="font-medium text-green-800">API-Daten geladen</p>
                    <p className="text-sm text-green-600">
                      {profileData.name} · {profileData.followers} Follower · {profileData.posts_count} Beiträge
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Bio:</h4>
                <p className="text-gray-600 text-sm">{profileData.bio || 'Keine Bio vorhanden'}</p>
              </div>

              <button
                onClick={handleAnalyzeWithApi}
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="analyze-with-api-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Analysiere...
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    Jetzt analysieren
                  </>
                )}
              </button>
            </div>
          ) : (
            <ProfileForm
              platform={selectedPlatform}
              initialData={profileData}
              onSubmit={handleAnalyze}
              loading={loading}
            />
          )}
        </div>
      )}

      {/* Analyse-Ergebnis */}
      {analysis && (
        <div className="space-y-2">
          <h2 className="font-semibold text-gray-800 text-lg flex items-center gap-2">
            3. Analyse-Ergebnis
            <span className="text-sm font-normal text-gray-500 capitalize">({selectedPlatform})</span>
          </h2>
          <AnalysisResult analysis={analysis} platform={selectedPlatform} />
        </div>
      )}
      </>
      )}

      {/* ============ WETTBEWERBER-VERGLEICH TAB ============ */}
      {activeTab === 'compare' && (
      <>
      {/* Eigenes Profil für Vergleich */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <User size={20} className="text-orange-500" />
          1. Dein Profil
        </h2>
        
        {/* Option: Aus Historie wählen */}
        {history.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Letzte Analyse verwenden (optional):
            </label>
            <select
              value={ownProfileForCompare.selectedHistoryId}
              onChange={(e) => {
                if (e.target.value) {
                  const item = history.find(h => h.analyzed_at === e.target.value);
                  if (item) handleSelectHistoryForCompare(item);
                } else {
                  setOwnProfileForCompare(prev => ({ ...prev, useHistory: false, selectedHistoryId: '' }));
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              data-testid="history-select"
            >
              <option value="">Keine (manuell eingeben)</option>
              {history.map((item, index) => (
                <option key={index} value={item.analyzed_at}>
                  {item.profile_data?.name || item.platform} - {new Date(item.analyzed_at).toLocaleDateString('de-DE')}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Manuelle Eingabe */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Profilname *</label>
              <input
                type="text"
                value={ownProfileForCompare.name}
                onChange={(e) => updateOwnProfile('name', e.target.value)}
                placeholder="z.B. Förderfuchs"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                data-testid="own-profile-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plattform</label>
              <select
                value={ownProfileForCompare.platform}
                onChange={(e) => updateOwnProfile('platform', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">Plattform wählen</option>
                {PLATFORM_LIST.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bio / Headline *</label>
            <textarea
              value={ownProfileForCompare.bio}
              onChange={(e) => updateOwnProfile('bio', e.target.value)}
              placeholder="Deine aktuelle Bio hier einfügen..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              data-testid="own-profile-bio"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung (optional)</label>
            <textarea
              value={ownProfileForCompare.description}
              onChange={(e) => updateOwnProfile('description', e.target.value)}
              placeholder="Weitere Informationen über dein Profil..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>
      </div>

      {/* Wettbewerber-Eingabe */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-800 flex items-center gap-2">
            <Users size={20} className="text-blue-500" />
            2. Wettbewerber (1-3)
          </h2>
          {competitors.length < 3 && (
            <button
              onClick={addCompetitor}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
              data-testid="add-competitor-btn"
            >
              <Plus size={16} />
              Wettbewerber hinzufügen
            </button>
          )}
        </div>

        <div className="space-y-6">
          {competitors.map((competitor, index) => (
            <div 
              key={index} 
              className="p-4 bg-gray-50 rounded-lg border border-gray-200"
              data-testid={`competitor-block-${index}`}
            >
              <div className="flex items-center justify-between mb-3">
                <input
                  type="text"
                  value={competitor.label}
                  onChange={(e) => updateCompetitor(index, 'label', e.target.value)}
                  className="font-medium text-gray-800 bg-transparent border-none focus:outline-none focus:ring-0 p-0"
                  placeholder={`Wettbewerber ${index + 1}`}
                />
                {competitors.length > 1 && (
                  <button
                    onClick={() => removeCompetitor(index)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    data-testid={`remove-competitor-${index}`}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Name</label>
                    <input
                      type="text"
                      value={competitor.name}
                      onChange={(e) => updateCompetitor(index, 'name', e.target.value)}
                      placeholder="Name des Wettbewerbers"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">URL (optional)</label>
                    <input
                      type="url"
                      value={competitor.url}
                      onChange={(e) => updateCompetitor(index, 'url', e.target.value)}
                      placeholder="https://..."
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">Bio / Profiltext *</label>
                  <textarea
                    value={competitor.bio}
                    onChange={(e) => updateCompetitor(index, 'bio', e.target.value)}
                    placeholder="Bio oder Profiltext des Wettbewerbers hier einfügen..."
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    data-testid={`competitor-bio-${index}`}
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">Beschreibung (optional)</label>
                  <textarea
                    value={competitor.description}
                    onChange={(e) => updateCompetitor(index, 'description', e.target.value)}
                    placeholder="Weitere Infos zum Wettbewerber..."
                    rows={1}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Vergleich starten Button */}
        <button
          onClick={handleCompare}
          disabled={compareLoading}
          className="w-full mt-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="start-compare-btn"
        >
          {compareLoading ? (
            <>
              <Loader2 className="animate-spin" size={18} />
              Vergleiche mit KI...
            </>
          ) : (
            <>
              <Scale size={18} />
              Vergleich starten
            </>
          )}
        </button>
      </div>

      {/* Vergleichs-Ergebnis */}
      {comparisonResult && comparisonResult.comparison && (
        <ComparisonResult comparison={comparisonResult.comparison} />
      )}
      </>
      )}
    </div>
  );
};

// Vergleichs-Ergebnis Komponente
const ComparisonResult = ({ comparison }) => {
  const [copiedBio, setCopiedBio] = useState(null);

  const copyBio = (bio, index) => {
    navigator.clipboard.writeText(bio);
    setCopiedBio(index);
    toast.success('Bio kopiert!');
    setTimeout(() => setCopiedBio(null), 2000);
  };

  return (
    <div className="space-y-6" data-testid="comparison-result">
      {/* Zusammenfassung */}
      <div className="bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl p-6 text-white">
        <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
          <Scale size={20} />
          Vergleichs-Zusammenfassung
        </h3>
        <p className="leading-relaxed opacity-95">{comparison.comparison_summary}</p>
      </div>

      {/* Unterschiede */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <AlertTriangle className="text-amber-500" size={18} />
          Wichtigste Unterschiede
        </h3>
        <ul className="space-y-3">
          {(comparison.differences || []).map((diff, index) => (
            <li key={index} className="flex items-start gap-3">
              <span className="w-6 h-6 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium">
                {index + 1}
              </span>
              <p className="text-gray-600">{diff}</p>
            </li>
          ))}
        </ul>
      </div>

      {/* Differenzierungs-Ideen */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Sparkles className="text-green-500" size={18} />
          So kannst du dich abheben
        </h3>
        <ul className="space-y-3">
          {(comparison.differentiation_ideas || []).map((idea, index) => (
            <li key={index} className="flex items-start gap-3">
              <span className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium">
                {index + 1}
              </span>
              <p className="text-gray-600">{idea}</p>
            </li>
          ))}
        </ul>
      </div>

      {/* Neue Bio-Vorschläge */}
      {comparison.suggested_bios?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <MessageSquare className="text-blue-500" size={18} />
            Neue Bio-/Headline-Vorschläge
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Diese Bios sind so formuliert, dass du dich klar von deinen Wettbewerbern abhebst:
          </p>
          <div className="space-y-3">
            {comparison.suggested_bios.map((bio, index) => (
              <div 
                key={index} 
                className="p-4 bg-blue-50 rounded-lg border border-blue-200 hover:border-blue-400 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-gray-700 text-sm flex-1">{bio}</p>
                  <button
                    onClick={() => copyBio(bio, index)}
                    className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                      copiedBio === index 
                        ? 'bg-green-100 text-green-600' 
                        : 'hover:bg-blue-200 text-blue-500'
                    }`}
                    data-testid={`copy-compare-bio-${index}`}
                  >
                    {copiedBio === index ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
                <p className="text-xs text-blue-400 mt-2">{bio.length} Zeichen</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileOptimizerPage;
